/**
 * Component Palette
 *
 * Draggable component palette for adding HVAC components to the graph.
 * Components are organized by category with search/filter functionality.
 */

import { useState, useCallback, useMemo } from 'react';
import type { ComponentCategory, HVACComponentDefinition } from './types';
import {
  HVAC_COMPONENTS,
  CATEGORIES,
  CATEGORY_NAMES,
  getComponentsByCategory,
} from './components';

interface ComponentPaletteProps {
  /** Callback when a component is dragged */
  onDragStart?: (componentType: string) => void;
}

/**
 * Get icon SVG for palette
 */
function PaletteIcon({ icon, color }: { icon: string; color: string }) {
  const commonProps = {
    className: 'h-5 w-5',
    fill: color,
  };

  switch (icon) {
    case 'fan':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M12 11c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-1-7v4l4 2-4 2v4h2v-3l3-1.5-3-1.5V4h-2z" />
        </svg>
      );
    case 'snowflake':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M22 11h-4.17l3.24-3.24-1.41-1.42L15 11h-2V9l4.66-4.66-1.42-1.41L13 6.17V2h-2v4.17L7.76 2.93 6.34 4.34 11 9v2H9L4.34 6.34 2.93 7.76 6.17 11H2v2h4.17l-3.24 3.24 1.41 1.42L9 13h2v2l-4.66 4.66 1.42 1.41L11 17.83V22h2v-4.17l3.24 3.24 1.42-1.41L13 15v-2h2l4.66 4.66 1.41-1.42L17.83 13H22v-2z" />
        </svg>
      );
    case 'flame':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M12 23c3.9 0 7-3.1 7-7 0-2.8-1.6-4.7-2.8-6.4C14.5 7.3 13 5.1 13 2c0-.6-.4-1-1-1-.5 0-.9.4-1 .9C10.6 5.5 8 8.3 6.4 11 5.5 12.4 5 14.1 5 16c0 3.9 3.1 7 7 7z" />
        </svg>
      );
    case 'box':
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <path d="M21 16.5c0 .38-.21.71-.53.88l-7.9 4.44c-.16.12-.36.18-.57.18-.21 0-.41-.06-.57-.18l-7.9-4.44A.991.991 0 013 16.5v-9c0-.38.21-.71.53-.88l7.9-4.44c.16-.12.36-.18.57-.18.21 0 .41.06.57.18l7.9 4.44c.32.17.53.5.53.88v9z" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" {...commonProps}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
        </svg>
      );
  }
}

/**
 * Single component item in the palette
 */
function ComponentItem({
  component,
  onDragStart,
}: {
  component: HVACComponentDefinition;
  onDragStart: ((componentType: string) => void) | undefined;
}) {
  const handleDragStart = useCallback(
    (event: React.DragEvent) => {
      event.dataTransfer.setData('application/reactflow', component.type);
      event.dataTransfer.effectAllowed = 'move';
      onDragStart?.(component.type);
    },
    [component.type, onDragStart]
  );

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className="flex cursor-grab items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 shadow-sm transition-all hover:border-gray-300 hover:shadow active:cursor-grabbing"
      title={component.description}
    >
      <div
        className="flex h-7 w-7 items-center justify-center rounded"
        style={{ backgroundColor: `${component.color}20` }}
      >
        <PaletteIcon icon={component.icon} color={component.color} />
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="truncate text-xs font-medium text-gray-700">{component.displayName}</div>
      </div>
    </div>
  );
}

/**
 * Category section in the palette
 */
function CategorySection({
  category,
  components,
  expanded,
  onToggle,
  onDragStart,
}: {
  category: ComponentCategory;
  components: HVACComponentDefinition[];
  expanded: boolean;
  onToggle: () => void;
  onDragStart: ((componentType: string) => void) | undefined;
}) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
      >
        <span className="text-sm font-medium text-gray-700">{CATEGORY_NAMES[category]}</span>
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <span>{components.length}</span>
          <svg
            className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
          </svg>
        </span>
      </button>

      {expanded && (
        <div className="flex flex-col gap-1 px-2 pb-2">
          {components.map((component) => (
            <ComponentItem
              key={component.type}
              component={component}
              onDragStart={onDragStart}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Component Palette Component
 */
export function ComponentPalette({ onDragStart }: ComponentPaletteProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<ComponentCategory>>(
    new Set(['fans', 'cooling-coils', 'heating-coils'])
  );

  const toggleCategory = useCallback((category: ComponentCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  // Filter components by search query
  const filteredComponents = useMemo(() => {
    if (!searchQuery.trim()) {
      return null; // Show categorized view
    }

    const query = searchQuery.toLowerCase();
    return HVAC_COMPONENTS.filter(
      (c) =>
        c.displayName.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query) ||
        c.idfClass.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  return (
    <div className="flex h-full flex-col bg-gray-50">
      {/* Header */}
      <div className="border-b bg-white px-3 py-2">
        <h2 className="text-sm font-semibold text-gray-800">Components</h2>
        <p className="text-xs text-gray-500">Drag components onto the canvas</p>
      </div>

      {/* Search */}
      <div className="border-b bg-white p-2">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); }}
            placeholder="Search components..."
            className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <svg
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Component list */}
      <div className="flex-1 overflow-y-auto">
        {filteredComponents ? (
          // Search results view
          <div className="p-2">
            {filteredComponents.length === 0 ? (
              <div className="py-4 text-center text-sm text-gray-500">
                No components found
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {filteredComponents.map((component) => (
                  <ComponentItem
                    key={component.type}
                    component={component}
                    onDragStart={onDragStart}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          // Categorized view
          <div>
            {CATEGORIES.map((category) => {
              const components = getComponentsByCategory(category);
              return (
                <CategorySection
                  key={category}
                  category={category}
                  components={components}
                  expanded={expandedCategories.has(category)}
                  onToggle={() => { toggleCategory(category); }}
                  onDragStart={onDragStart}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="border-t bg-white px-3 py-2">
        <div className="text-xs text-gray-500">
          <span className="font-medium">Tip:</span> Connect outlets (right) to inlets (left)
        </div>
      </div>
    </div>
  );
}
